import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Search, Filter, 
  FileText, Image, Calendar, User, Newspaper, Save, X,
  Bold, Italic, Link2, List, ListOrdered, Quote, ImageIcon,
  Archive, RotateCcw, Star, AlertTriangle, RefreshCw
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

// Priority labels
const PRIORITY_LABELS = {
  1: { label: "Breaking", color: "bg-red-500 text-white" },
  2: { label: "Top Story", color: "bg-orange-500 text-black" },
  3: { label: "Featured", color: "bg-yellow-500 text-black" },
  4: { label: "Important", color: "bg-blue-500 text-white" },
  5: { label: "Normal", color: "bg-zinc-600 text-white" },
  6: { label: "Normal", color: "bg-zinc-600 text-white" },
  7: { label: "Low", color: "bg-zinc-700 text-zinc-300" },
  8: { label: "Low", color: "bg-zinc-700 text-zinc-300" },
  9: { label: "Archive Soon", color: "bg-zinc-800 text-zinc-400" },
  10: { label: "Filler", color: "bg-zinc-800 text-zinc-500" }
};

// Simple Rich Text Editor Component
const RichTextEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  return (
    <div className="border border-white/10">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-zinc-800 border-b border-white/10">
        <button type="button" onClick={() => execCommand("bold")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Bold">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand("italic")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Italic">
          <Italic className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => { const url = prompt("Enter URL:"); if (url) execCommand("createLink", url); }} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Insert Link">
          <Link2 className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand("insertUnorderedList")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Bullet List">
          <List className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand("insertOrderedList")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Numbered List">
          <ListOrdered className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand("formatBlock", "blockquote")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Quote">
          <Quote className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => { const url = prompt("Enter image URL:"); if (url) execCommand("insertImage", url); }} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white" title="Insert Image">
          <ImageIcon className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => execCommand("formatBlock", "h2")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white text-xs font-bold" title="Heading">H2</button>
        <button type="button" onClick={() => execCommand("formatBlock", "h3")} className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white text-xs font-bold" title="Subheading">H3</button>
      </div>
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        dangerouslySetInnerHTML={{ __html: value }}
        className="min-h-[300px] p-4 bg-black text-white focus:outline-none prose prose-invert max-w-none"
        style={{ whiteSpace: "pre-wrap" }}
      />
    </div>
  );
};

export const NewsTab = () => {
  const [articles, setArticles] = useState([]);
  const [trashedArticles, setTrashedArticles] = useState([]);
  const [archivedArticles, setArchivedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeView, setActiveView] = useState("articles"); // articles, archived, trash

  // Form state
  const [form, setForm] = useState({
    title: "",
    content: "",
    excerpt: "",
    featured_image: "",
    category: "General",
    tags: [],
    status: "draft",
    priority: 5
  });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchArticles();
    fetchArchivedArticles();
    fetchTrashedArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/news/admin/articles`, { headers: getAuthHeader() });
      // Filter out archived and trashed articles - res.data.articles is the array
      const articlesArray = res.data.articles || res.data || [];
      setArticles(articlesArray.filter(a => a.status !== 'archived' && a.status !== 'trash'));
    } catch (err) {
      console.error("Error fetching articles:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedArticles = async () => {
    try {
      const res = await axios.get(`${API}/news/admin/articles/archived`, { headers: getAuthHeader() });
      setArchivedArticles(res.data);
    } catch (err) {
      console.error("Error fetching archived articles:", err);
    }
  };

  const fetchTrashedArticles = async () => {
    try {
      const res = await axios.get(`${API}/news/admin/articles/trash`, { headers: getAuthHeader() });
      setTrashedArticles(res.data);
    } catch (err) {
      console.error("Error fetching trashed articles:", err);
    }
  };

  const resetForm = () => {
    setForm({
      title: "",
      content: "",
      excerpt: "",
      featured_image: "",
      category: "General",
      tags: [],
      status: "draft",
      priority: 5
    });
    setTagInput("");
    setEditingArticle(null);
  };

  const handleEdit = (article) => {
    setForm({
      title: article.title,
      content: article.content,
      excerpt: article.excerpt || "",
      featured_image: article.featured_image || "",
      category: article.category || "General",
      tags: article.tags || [],
      status: article.status,
      priority: article.priority || 5
    });
    setEditingArticle(article);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.content) {
      alert("Title and content are required");
      return;
    }

    setSaving(true);
    try {
      if (editingArticle) {
        await axios.put(`${API}/news/admin/articles/${editingArticle.id}`, form, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/news/admin/articles`, form, { headers: getAuthHeader() });
      }
      fetchArticles();
      fetchArchivedArticles();
      setShowEditor(false);
      resetForm();
    } catch (err) {
      console.error("Error saving article:", err);
      alert("Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm("Move this article to trash?")) return;
    try {
      await axios.delete(`${API}/news/admin/articles/${articleId}`, { headers: getAuthHeader() });
      fetchArticles();
      fetchTrashedArticles();
    } catch (err) {
      console.error("Error deleting article:", err);
    }
  };

  const handleArchive = async (articleId) => {
    try {
      await axios.post(`${API}/news/admin/articles/${articleId}/archive`, {}, { headers: getAuthHeader() });
      fetchArticles();
      fetchArchivedArticles();
    } catch (err) {
      console.error("Error archiving article:", err);
    }
  };

  const handleRestore = async (articleId) => {
    try {
      await axios.post(`${API}/news/admin/articles/${articleId}/restore`, {}, { headers: getAuthHeader() });
      fetchArticles();
      fetchArchivedArticles();
      fetchTrashedArticles();
    } catch (err) {
      console.error("Error restoring article:", err);
    }
  };

  const handleRepost = async (articleId) => {
    if (!window.confirm("Republish this article with a new date?")) return;
    try {
      await axios.post(`${API}/news/admin/articles/${articleId}/repost`, {}, { headers: getAuthHeader() });
      fetchArticles();
      fetchArchivedArticles();
    } catch (err) {
      console.error("Error reposting article:", err);
    }
  };

  const handlePermanentDelete = async (articleId) => {
    if (!window.confirm("Permanently delete this article? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/news/admin/articles/${articleId}/permanent`, { headers: getAuthHeader() });
      fetchTrashedArticles();
    } catch (err) {
      console.error("Error permanently deleting article:", err);
    }
  };

  const handleEmptyTrash = async () => {
    if (!window.confirm("Permanently delete ALL trashed articles? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/news/admin/articles/trash/empty`, { headers: getAuthHeader() });
      fetchTrashedArticles();
    } catch (err) {
      console.error("Error emptying trash:", err);
    }
  };

  const handleDeleteAllArticles = async () => {
    if (!window.confirm("⚠️ WARNING: This will permanently delete ALL articles (manual and RSS). This cannot be undone. Are you sure?")) return;
    if (!window.confirm("This is your last chance to cancel. Type 'delete all' in the next prompt to confirm.")) return;
    const confirmation = window.prompt("Type 'delete all' to confirm:");
    if (confirmation !== "delete all") {
      alert("Deletion cancelled.");
      return;
    }
    try {
      const res = await axios.delete(`${API}/news/admin/articles/all`, { headers: getAuthHeader() });
      alert(`Deleted ${res.data.deleted_count} articles.`);
      fetchArticles();
      fetchArchivedArticles();
      fetchTrashedArticles();
    } catch (err) {
      console.error("Error deleting all articles:", err);
      alert("Failed to delete articles");
    }
  };

  const handleFetchRSSFeeds = async () => {
    try {
      const res = await axios.post(`${API}/news/admin/rss/scheduler/fetch-now`, {}, { headers: getAuthHeader() });
      alert(`RSS fetch triggered! ${res.data.articles_imported || 0} new articles imported.`);
      fetchArticles();
    } catch (err) {
      console.error("Error fetching RSS feeds:", err);
      alert("Failed to fetch RSS feeds");
    }
  };

  const handleAddTag = () => {
    if (tagInput && !form.tags.includes(tagInput)) {
      setForm({ ...form, tags: [...form.tags, tagInput] });
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter((t) => t !== tag) });
  };

  const getFilteredArticles = () => {
    let list = activeView === "archived" ? archivedArticles : activeView === "trash" ? trashedArticles : articles;
    
    return list.filter((article) => {
      const matchesFilter = filter === "all" || article.status === filter || 
        (filter === "writer" && article.author_type === "writer") ||
        (filter === "rss" && article.author_type === "rss") ||
        (filter === "top" && article.priority <= 3);
      const matchesSearch = !searchQuery || 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.author_name?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDaysInTrash = (trashedAt) => {
    if (!trashedAt) return "";
    const days = Math.floor((Date.now() - new Date(trashedAt).getTime()) / (1000 * 60 * 60 * 24));
    const remaining = 14 - days;
    return remaining > 0 ? `${remaining} days left` : "Deleting soon";
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  // Article Editor View
  if (showEditor) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold uppercase text-white">
            {editingArticle ? "Edit Article" : "New Article"}
          </h2>
          <div className="flex items-center gap-2">
            <Button onClick={() => { setShowEditor(false); resetForm(); }} variant="ghost" className="text-zinc-400 hover:text-white">
              <X className="w-4 h-4 mr-2" />Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-white text-black hover:bg-zinc-200">
              <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Article"}
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Article title..."
              className="w-full bg-black border border-white/10 text-white px-4 py-3 text-lg focus:outline-none focus:border-white/30"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
              Priority <span className="text-orange-500">(1 = Breaking News, 10 = Low Priority)</span>
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="1"
                max="10"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) })}
                className="flex-1 h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer"
              />
              <span className={`px-3 py-1 text-xs font-bold ${PRIORITY_LABELS[form.priority]?.color || 'bg-zinc-600 text-white'}`}>
                {form.priority} - {PRIORITY_LABELS[form.priority]?.label || 'Normal'}
              </span>
            </div>
            {form.priority <= 3 && (
              <p className="text-xs text-orange-500 mt-1">
                <Star className="w-3 h-3 inline mr-1" />
                This article will appear in Top Stories
              </p>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Content *</label>
            <RichTextEditor value={form.content} onChange={(content) => setForm({ ...form, content })} />
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Excerpt (optional)</label>
            <textarea
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              placeholder="Brief summary of the article..."
              rows={3}
              className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-white/30 resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Featured Image */}
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Featured Image URL</label>
              <input
                type="url"
                value={form.featured_image}
                onChange={(e) => setForm({ ...form, featured_image: e.target.value })}
                placeholder="https://..."
                className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-white/30"
              />
              {form.featured_image && (
                <div className="mt-2 aspect-video bg-zinc-900 max-w-xs">
                  <img src={form.featured_image} alt="Preview" className="w-full h-full object-cover" onError={(e) => (e.target.style.display = "none")} />
                </div>
              )}
            </div>

            {/* Category (Backend only, not shown on frontend) */}
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Category (Internal Use)</label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="General"
                className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-white/30"
              />
              <p className="text-xs text-zinc-500 mt-1">Categories are for internal organization only</p>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())}
                placeholder="Add tag..."
                className="flex-1 bg-black border border-white/10 text-white px-4 py-2 focus:outline-none focus:border-white/30"
              />
              <Button type="button" onClick={handleAddTag} className="bg-zinc-800 text-white hover:bg-zinc-700">Add</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 px-3 py-1 bg-zinc-800 text-zinc-300 text-sm">
                  {tag}
                  <button type="button" onClick={() => handleRemoveTag(tag)} className="text-zinc-500 hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Status</label>
            <div className="flex gap-2">
              {["draft", "published"].map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setForm({ ...form, status })}
                  className={`px-4 py-2 text-sm font-bold uppercase ${
                    form.status === status
                      ? status === "published" ? "bg-green-600 text-white" : "bg-yellow-600 text-black"
                      : "bg-zinc-800 text-zinc-400 hover:text-white"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Articles List View
  const filteredArticles = getFilteredArticles();

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase text-white">News Articles</h2>
          <p className="text-zinc-500 text-sm mt-1">
            {articles.length} active • {archivedArticles.length} archived • {trashedArticles.length} in trash
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleFetchRSSFeeds} variant="outline" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300">
            <RefreshCw className="w-4 h-4 mr-2" />Fetch RSS
          </Button>
          <Button onClick={handleDeleteAllArticles} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300">
            <Trash2 className="w-4 h-4 mr-2" />Delete All
          </Button>
          <Button onClick={() => setShowEditor(true)} className="bg-white text-black hover:bg-zinc-200">
            <Plus className="w-4 h-4 mr-2" />New Article
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex items-center gap-4 mb-4 border-b border-white/10 pb-4">
        <button
          onClick={() => setActiveView("articles")}
          className={`flex items-center gap-2 px-4 py-2 font-bold text-sm uppercase ${activeView === "articles" ? "bg-white text-black" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}
        >
          <Newspaper className="w-4 h-4" />
          Articles ({articles.length})
        </button>
        <button
          onClick={() => setActiveView("archived")}
          className={`flex items-center gap-2 px-4 py-2 font-bold text-sm uppercase ${activeView === "archived" ? "bg-white text-black" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}
        >
          <Archive className="w-4 h-4" />
          Archived ({archivedArticles.length})
        </button>
        <button
          onClick={() => setActiveView("trash")}
          className={`flex items-center gap-2 px-4 py-2 font-bold text-sm uppercase ${activeView === "trash" ? "bg-red-500 text-white" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}
        >
          <Trash2 className="w-4 h-4" />
          Trash ({trashedArticles.length})
        </button>
        {activeView === "trash" && trashedArticles.length > 0 && (
          <button onClick={handleEmptyTrash} className="ml-auto text-xs text-red-500 hover:text-red-400 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />Empty Trash
          </button>
        )}
      </div>

      {/* Filters */}
      {activeView === "articles" && (
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            {["all", "top", "published", "draft", "writer", "rss"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 text-xs font-bold uppercase ${
                  filter === f ? "bg-white text-black" : "bg-zinc-800 text-zinc-400 hover:text-white"
                }`}
              >
                {f === "top" ? "⭐ Top Stories" : f}
              </button>
            ))}
          </div>
          <div className="flex-1 max-w-xs">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-white/30"
              />
            </div>
          </div>
        </div>
      )}

      {/* Trash Notice */}
      {activeView === "trash" && (
        <div className="flex items-center gap-3 mb-6 p-4 bg-red-500/10 border border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <p className="text-red-400 text-sm">Articles in trash are automatically deleted after 14 days.</p>
        </div>
      )}

      {/* Articles Table */}
      <div className="bg-zinc-900/50 border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-xs font-bold uppercase text-zinc-400 px-4 py-3">Article</th>
              {activeView === "articles" && (
                <th className="text-center text-xs font-bold uppercase text-zinc-400 px-4 py-3 w-20">Priority</th>
              )}
              <th className="text-left text-xs font-bold uppercase text-zinc-400 px-4 py-3 hidden md:table-cell">Author</th>
              <th className="text-center text-xs font-bold uppercase text-zinc-400 px-4 py-3">
                {activeView === "trash" ? "Time Left" : "Status"}
              </th>
              <th className="text-right text-xs font-bold uppercase text-zinc-400 px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredArticles.map((article) => (
              <tr key={article.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {article.featured_image ? (
                      <img src={article.featured_image} alt="" className="w-12 h-8 object-cover bg-zinc-800" />
                    ) : (
                      <div className="w-12 h-8 bg-zinc-800 flex items-center justify-center">
                        <Newspaper className="w-4 h-4 text-zinc-600" />
                      </div>
                    )}
                    <div>
                      <p className="text-white font-medium line-clamp-1">{article.title}</p>
                      <p className="text-xs text-zinc-500">{formatDate(article.created_at)}</p>
                    </div>
                  </div>
                </td>
                {activeView === "articles" && (
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold ${PRIORITY_LABELS[article.priority || 5]?.color || 'bg-zinc-600 text-white'}`}>
                      {article.priority || 5}
                    </span>
                  </td>
                )}
                <td className="px-4 py-3 hidden md:table-cell">
                  <div className="flex items-center gap-2">
                    <User className="w-3 h-3 text-zinc-500" />
                    <span className="text-zinc-400 text-sm">{article.author_name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  {activeView === "trash" ? (
                    <span className="text-xs text-red-400">{formatDaysInTrash(article.trashed_at)}</span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase ${
                      article.status === "published" ? "bg-green-500/20 text-green-500" :
                      article.status === "archived" ? "bg-zinc-500/20 text-zinc-400" :
                      "bg-yellow-500/20 text-yellow-500"
                    }`}>
                      {article.status === "published" ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {article.status}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {activeView === "articles" && (
                      <>
                        <button onClick={() => handleEdit(article)} className="p-2 text-zinc-500 hover:text-white" title="Edit">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleArchive(article.id)} className="p-2 text-zinc-500 hover:text-yellow-500" title="Archive">
                          <Archive className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(article.id)} className="p-2 text-zinc-500 hover:text-red-500" title="Move to Trash">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {activeView === "archived" && (
                      <>
                        <button onClick={() => handleRepost(article.id)} className="p-2 text-zinc-500 hover:text-green-500" title="Repost (Republish)">
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleRestore(article.id)} className="p-2 text-zinc-500 hover:text-blue-500" title="Restore to Draft">
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(article.id)} className="p-2 text-zinc-500 hover:text-red-500" title="Move to Trash">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {activeView === "trash" && (
                      <>
                        <button onClick={() => handleRestore(article.id)} className="p-2 text-zinc-500 hover:text-green-500" title="Restore">
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <button onClick={() => handlePermanentDelete(article.id)} className="p-2 text-zinc-500 hover:text-red-500" title="Delete Permanently">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredArticles.length === 0 && (
          <div className="text-center py-12">
            <Newspaper className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">
              {activeView === "trash" ? "Trash is empty" : activeView === "archived" ? "No archived articles" : "No articles found"}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default NewsTab;
